/// <reference path="../adonisrc.ts" />
/// <reference path="../config/inertia.ts" />

import "./css/app.css";
import "./lib/prism";
import "streamdown/styles.css";
import "./css/streamdown.css";
import { isDynamicImportError, recoverDynamicImport } from "./lib/vite-preload-recovery";
import { StaleChunkFallback } from "~/components/stale-chunk-fallback";
import "./i18n";
import "~/lib/xsrf";
import * as Sentry from "@sentry/react";
import { normalizeSentryGrouping } from "~/lib/sentry-grouping";
import { type ComponentType } from "react";
import AppLayout from "~/layouts/app-layout";
import { createRoot } from "react-dom/client";
import { createInertiaApp, router } from "@inertiajs/react";
import type { Page } from "@inertiajs/core";
import { toast } from "sonner";
import { resolvePageComponent } from "@adonisjs/inertia/helpers";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NewVersionAvailableBanner } from "~/components/new-version-available-banner";
import { RootErrorBoundary } from "~/components/root-error-boundary";
import { TooltipProvider } from "~/components/ui/tooltip";
import { TransmitProvider } from "~/contexts/transmit-context";
import { initializeTheme } from "~/hooks/use-appearance";
import { logDashboardDiagnostic } from "~/lib/dashboard-diagnostics";
import { initializeFirebase, registerServiceWorker } from "~/lib/firebase";

const appLayoutExcludedPagePrefixes = [
  "auth/",
  "onboarding",
  "organization-inactive",
  "chat/chat-widget",
  "chat/chat-widget-token-handler",
  "chat/unified-widgets",
  "forms/public-show",
  "company/privacy",
  "transfers/show",
  "support/track",
  "admin/",
  "central/",
];

type LayoutDecisionPageProps = {
  auth?: {
    user?: { id?: string | number } | null;
  };
};

const hasAuthenticatedUser = (page?: Page<LayoutDecisionPageProps>) =>
  Boolean(page?.props.auth?.user);

const shouldUsePersistentAppLayout = (pageName: string, page?: Page<LayoutDecisionPageProps>) =>
  hasAuthenticatedUser(page) &&
  !appLayoutExcludedPagePrefixes.some((prefix) => pageName.startsWith(prefix));

// Route every page loader through stale-chunk recovery so a deploy that 404s the
// old hashed page chunk reloads to fresh assets instead of crashing navigation.
const pageModules = import.meta.glob<ComponentType>("./pages/**/*.tsx");
const recoverablePageModules = Object.fromEntries(
  Object.entries(pageModules).map(([path, load]) => [path, () => recoverDynamicImport(load)]),
);

let appName = import.meta.env.VITE_APP_NAME || "Airie";
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

if (sentryDsn && !Sentry.isInitialized()) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,
    enabled: import.meta.env.MODE === "production",
    beforeSend: normalizeSentryGrouping,
  });
}

const queryClient = new QueryClient();

if (typeof window !== "undefined") {
  initializeFirebase();
  registerServiceWorker();
}

if (window.location.hash === "#_=_") {
  window.history.replaceState(
    null,
    document.title,
    window.location.pathname + window.location.search,
  );
}

router.on("httpException", (event) => {
  const response = event.detail.response;
  const isBadGateway = response.status === 502;
  const isNonInertiaResponse = !response.headers["x-inertia"];

  if (!isBadGateway && !isNonInertiaResponse) {
    return;
  }

  const message = isBadGateway
    ? "La solicitud de Inertia falló con 502 Bad Gateway"
    : "La solicitud de Inertia recibió una respuesta no compatible";

  if (isNonInertiaResponse) {
    console.error(message, {
      status: response.status,
      headers: response.headers,
      data: response.data,
    });
  }

  Sentry.captureException(new Error(message), {
    tags: {
      inertia: "httpException",
      badGateway: String(isBadGateway),
      nonInertiaResponse: String(isNonInertiaResponse),
      status: String(response.status),
    },
    contexts: {
      response: {
        status: response.status,
        headers: response.headers,
        dataPreview: String(response.data).slice(0, 1000),
      },
      location: {
        href: window.location.href,
      },
    },
  });

  toast.error(
    isBadGateway
      ? "El servidor no está disponible temporalmente. Inténtalo nuevamente."
      : "Algo salió mal. Inténtalo nuevamente.",
  );

  return false;
});

createInertiaApp({
  progress: { color: "#5468FF" },

  title: (title) => `${title} - ${appName}`,

  resolve: (name) => {
    return resolvePageComponent(
      `./pages/${name}.tsx`,
      recoverablePageModules,
      (page: ComponentType) => page,
    );
  },

  layout: (name, page) => (shouldUsePersistentAppLayout(name, page) ? AppLayout : null),

  setup({ el, App, props }) {
    appName = props.initialPage.props.appName;

    const root = createRoot(el, {
      onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn("Uncaught error", error, errorInfo.componentStack);
      }),
      onCaughtError: Sentry.reactErrorHandler(),
      onRecoverableError: Sentry.reactErrorHandler(),
    });

    root.render(
      <RootErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <TransmitProvider onDiagnostic={logDashboardDiagnostic}>
            <TooltipProvider>
              <App {...props} />
              <NewVersionAvailableBanner />
            </TooltipProvider>
          </TransmitProvider>
        </QueryClientProvider>
      </RootErrorBoundary>,
    );
  },

  defaults: {
    visitOptions: () => ({ viewTransition: false }),
  },
}).catch((error: unknown) => {
  // The initial page failed to resolve. The wrapped page loader already tried a
  // bounded stale-chunk reload; reaching here means its budget is spent, so a
  // reload will not help right now. Render a recoverable fallback into the
  // Inertia root (which never mounted) instead of leaving a blank white screen.
  // Non-stale errors are re-thrown so they still reach Sentry/console.
  if (!isDynamicImportError(error)) {
    throw error;
  }

  const el = document.getElementById("app");
  if (el) {
    createRoot(el).render(<StaleChunkFallback />);
  }
});

initializeTheme();
